/**
 * The one HTTP client.
 *
 * Two callers, because the backend has exactly two namespaces (docs/API_CONTRACT.md):
 *
 *   apiV1<T>(path, init)          → /api/v1/**  · envelope `{data, meta}` · JWT bearer + X-Org-Id
 *   mcApi<T>(path, params, init)  → /api/**     · Metricool-identical · raw payload · userToken
 *
 * The access token lives in memory only (never localStorage). The refresh token is an httpOnly
 * cookie, so a 401 is repaired by a single-flight POST /auth/refresh and one transparent retry.
 */

import type { ApiErrorBody, ApiMeta, ApiOk, AuthSession } from '@ftk-connector/shared'

import { messageFor } from './i18n/catalog'
import type { SimpleTKey } from './i18n/catalog'
import { errorKeyFor } from './i18n/errorCodes'
import { getLocale } from './i18n/locale'
import type { MessageParams, ParamValue } from './i18n/shared'
import { formatMessage, intlLocaleOf } from './i18n/shared'

const env = import.meta.env as Readonly<Record<string, string | undefined>>

/** Empty in dev — Vite proxies /api to the API origin. Set VITE_API_BASE for split deploys. */
export const API_BASE = env['VITE_API_BASE'] ?? ''

/* ── errors ───────────────────────────────────────────────────────────────── */

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: Record<string, unknown> | undefined
  /**
   * The sentence to show, named rather than spelled — null when there is only the server's own.
   *
   * Two things set it. This module, when it invented the sentence itself (a bare non-2xx, a
   * malformed envelope, a dead connection). And `ERROR_CODE_KEYS`, when the server's `code` is one
   * somebody has translated: per docs/I18N.md the error body's stable `code` is what the UI
   * translates by, and `message` is the fallback for a code nobody has. Both paths end here so
   * `errorMessage()` has one question to ask.
   *
   * Null therefore means *this code has no translation*, and the server's English is shown as sent
   * — which is the documented behaviour, not a missing feature.
   *
   * The key is carried rather than resolved, because an `ApiError` outlives the render that
   * created it: TanStack Query caches it, and a reader who switches language must see the
   * failure re-read in the new one. Baking the text here would freeze the language of every
   * cached error at the moment it was thrown.
   */
  readonly messageKey: SimpleTKey | null

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
    messageKey?: SimpleTKey | null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.messageKey = messageKey ?? null
  }

  /** The session is gone or the role is insufficient. */
  get isAuthProblem(): boolean {
    return this.status === 401 || this.status === 403
  }

  /** Nothing reached the server — worth offering a retry rather than a sign-in. */
  get isOffline(): boolean {
    return this.status === 0
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError

/**
 * Human sentence for a bare HTTP status, in the interface's voice. Never apologises.
 *
 * Named, not spelled: the status picks a catalogue key and the key is resolved when someone
 * looks at it. `common.states.errorBody` and `.offlineBody` are reused rather than duplicated —
 * they are word for word the sentence an error panel already shows for the same two failures.
 */
const statusKey = (status: number): SimpleTKey => {
  switch (status) {
    case 400:
      return 'common.errors.badRequest'
    case 401:
      return 'common.errors.unauthorized'
    case 403:
      return 'common.errors.forbidden'
    case 404:
      return 'common.errors.notFound'
    case 409:
      return 'common.errors.conflict'
    case 422:
      return 'common.errors.validation'
    case 429:
      return 'common.errors.rateLimited'
    case 501:
      return 'common.errors.notImplemented'
    default:
      return status >= 500 ? 'common.states.errorBody' : 'common.errors.request'
  }
}

/**
 * The English text of a key. This is what `Error.message` carries, so a stack trace, a log line
 * and a bug report stay in one language whatever the reader has chosen.
 */
const englishText = (key: SimpleTKey): string =>
  formatMessage(messageFor('en', key), intlLocaleOf('en'))

/** The same key in the language the reader is actually using. Resolved at render, never stored. */
const localeText = (key: SimpleTKey, params?: MessageParams): string => {
  const locale = getLocale()
  return formatMessage(messageFor(locale, key), intlLocaleOf(locale), params)
}

/**
 * The scalar entries of an error's `details`, which are the only ones a sentence can name.
 *
 * `FLOW_LIMIT` answers with `{ limit: 12 }` and `FLOW_NAME_TAKEN` with `{ name: 'Daily digest' }`;
 * a translation that could not say the number would have to say "a limit", which is worse in both
 * languages. Everything that is not a string or a number — the id arrays, the Zod field map — is
 * dropped rather than stringified, because `{"0":"OWNER"}` in the middle of a sentence is noise.
 *
 * Extra entries are harmless: `formatMessage` fills the placeholders the message has and ignores
 * the rest. The reverse is not harmless, which is why `ERROR_CODE_KEYS` only uses a placeholder
 * for a detail the backend always sends.
 */
const detailParams = (details: Record<string, unknown> | undefined): MessageParams | undefined => {
  if (details === undefined) return undefined
  const params: Record<string, ParamValue> = {}
  for (const [name, value] of Object.entries(details)) {
    if (typeof value === 'string' || typeof value === 'number') params[name] = value
  }
  return params
}

/* ── in-memory session state ──────────────────────────────────────────────── */

let accessToken: string | null = null
let activeOrgId: string | null = null
let mcUserToken: string | null = null
let mcUserId: number | null = null
let activeBlogId: number | null = null

type Listener = () => void
const unauthorizedListeners = new Set<Listener>()

/** AuthProvider subscribes so a dead refresh cookie drops the session exactly once. */
export const onUnauthorized = (fn: Listener): (() => void) => {
  unauthorizedListeners.add(fn)
  return () => {
    unauthorizedListeners.delete(fn)
  }
}

const notifyUnauthorized = (): void => {
  for (const fn of unauthorizedListeners) fn()
}

export const setAccessToken = (token: string | null): void => {
  accessToken = token
}

export const getAccessToken = (): string | null => accessToken

export const setActiveOrgId = (orgId: string | null): void => {
  activeOrgId = orgId
}

export const getActiveOrgId = (): string | null => activeOrgId

/** The Metricool-compatible triple used by every /api/** call. */
export const setMetricoolIdentity = (userToken: string | null, userId: number | null): void => {
  mcUserToken = userToken
  mcUserId = userId
}

export const setActiveBlogId = (blogId: number | null): void => {
  activeBlogId = blogId
}

export const getActiveBlogId = (): number | null => activeBlogId

/** Records a fresh session across both namespaces in one call. */
export const applySession = (session: AuthSession): void => {
  accessToken = session.accessToken
  activeOrgId = session.user.activeOrgId
  mcUserToken = session.user.userToken
  mcUserId = session.user.mcUserId
  if (activeBlogId === null) activeBlogId = session.user.activeBlogId
}

export const clearSession = (): void => {
  accessToken = null
  activeOrgId = null
  mcUserToken = null
  mcUserId = null
  activeBlogId = null
}

/* ── query strings ────────────────────────────────────────────────────────── */

export type QueryValue = string | number | boolean | null | undefined
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>

export const buildQuery = (params?: QueryParams): string => {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, raw] of Object.entries(params)) {
    if (raw === null || raw === undefined) continue
    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (item === null || item === undefined) continue
        search.append(key, String(item))
      }
      continue
    }
    search.append(key, String(raw as QueryValue))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/* ── request plumbing ─────────────────────────────────────────────────────── */

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON with the right Content-Type. Use instead of `body`. */
  json?: unknown
  body?: BodyInit | null
  query?: QueryParams
  /** Public endpoint: send no Authorization header and never attempt a refresh. */
  anonymous?: boolean
}

const parseBody = async (res: Response): Promise<unknown> => {
  const text = await res.text()
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

const isErrorEnvelope = (body: unknown): body is ApiErrorBody => {
  if (typeof body !== 'object' || body === null || !('error' in body)) return false
  const err = (body as { error: unknown }).error
  return typeof err === 'object' && err !== null && 'code' in err && 'message' in err
}

/* single-flight refresh — a burst of 401s produces exactly one /auth/refresh */
let refreshInFlight: Promise<string | null> | null = null

const requestRefresh = async (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight
  const run = (async (): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) return null
      const body = await parseBody(res)
      if (isErrorEnvelope(body)) return null
      if (typeof body !== 'object' || body === null || !('data' in body)) return null
      const session = (body as ApiOk<AuthSession>).data
      applySession(session)
      return session.accessToken
    } catch {
      return null
    }
  })()
  refreshInFlight = run
  void run.finally(() => {
    refreshInFlight = null
  })
  return run
}

/** Forces a refresh from outside — used by AuthProvider on boot. */
export const refreshSession = (): Promise<string | null> => requestRefresh()

const send = async (url: string, init: ApiRequestInit, headers: Headers): Promise<Response> => {
  // `query`, `anonymous` and `headers` are consumed by the callers; strip them from RequestInit.
  const { json, query, anonymous, headers: initHeaders, body, ...rest } = init
  void query
  void anonymous
  void initHeaders
  try {
    return await fetch(url, {
      ...rest,
      headers,
      credentials: 'include',
      body: json !== undefined ? JSON.stringify(json) : (body ?? null),
    })
  } catch {
    throw new ApiError(
      0,
      'NETWORK_ERROR',
      englishText('common.states.offlineBody'),
      undefined,
      'common.states.offlineBody',
    )
  }
}

/* ── /api/v1 — our surface, enveloped ─────────────────────────────────────── */

const v1Headers = (init: ApiRequestInit, accept: string): Headers => {
  const headers = new Headers(init.headers)
  headers.set('Accept', accept)
  if (init.json !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (!init.anonymous) {
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    if (activeOrgId) headers.set('X-Org-Id', activeOrgId)
  }
  return headers
}

const v1Url = (path: string, init: ApiRequestInit): string =>
  `${API_BASE}/api/v1${path}${buildQuery(init.query)}`

const fetchV1 = async (path: string, init: ApiRequestInit, allowRetry: boolean): Promise<unknown> => {
  const headers = v1Headers(init, 'application/json')
  const url = v1Url(path, init)
  const res = await send(url, init, headers)

  if (res.status === 401 && allowRetry && !init.anonymous) {
    const token = await requestRefresh()
    if (token) return fetchV1(path, init, false)
    notifyUnauthorized()
  }

  const body = await parseBody(res)

  if (isErrorEnvelope(body)) {
    throw new ApiError(
      res.status,
      body.error.code,
      body.error.message,
      body.error.details,
      errorKeyFor(body.error.code),
    )
  }
  if (!res.ok) {
    const key = statusKey(res.status)
    throw new ApiError(res.status, `HTTP_${res.status}`, englishText(key), undefined, key)
  }
  return body
}

/** Full envelope, when you need `meta` (pagination, data source). */
export const apiV1Envelope = async <T>(path: string, init: ApiRequestInit = {}): Promise<ApiOk<T>> => {
  const body = await fetchV1(path, init, true)
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    throw new ApiError(
      500,
      'MALFORMED_RESPONSE',
      englishText('common.errors.malformed'),
      undefined,
      'common.errors.malformed',
    )
  }
  return body as ApiOk<T>
}

/** The default caller: unwraps `{data}` and throws `ApiError` on `{error}`. */
export const apiV1 = async <T>(path: string, init: ApiRequestInit = {}): Promise<T> => {
  const envelope = await apiV1Envelope<T>(path, init)
  return envelope.data
}

/**
 * For an endpoint that answers `204 No Content`.
 *
 * `apiV1` cannot serve one: it unwraps `{data}` and treats an empty body as `MALFORMED_RESPONSE`,
 * which is right for every endpoint that is supposed to answer something and wrong for the ones
 * whose whole contract is that they answer nothing. The SmartLink tracking endpoint is the first
 * of those — it returns 204 to *every* input, deliberately, so that it cannot be used to discover
 * which slugs exist. A caller that threw on its success would swallow the distinction between
 * that success and a real failure.
 *
 * Failures still throw: `fetchV1` raises `ApiError` for an error envelope and for a bare non-2xx.
 */
export const apiV1Void = async (path: string, init: ApiRequestInit = {}): Promise<void> => {
  await fetchV1(path, init, true)
}

/** A file the server produced, with the name it chose for it. */
export interface ApiFile {
  blob: Blob
  filename: string
}

const FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

/**
 * For an endpoint whose body is a *file* rather than an envelope — the report exporter is the
 * only one, and it will stay that way for as long as this product has one thing worth
 * downloading.
 *
 * It shares `send`, the headers and the single-flight refresh with `fetchV1` on purpose: an
 * export that ran for ninety seconds and then 401'd because an access token expired mid-render
 * would be the most expensive way in the product to lose a session. What it cannot share is the
 * body handling — `fetchV1` reads the response as text, which would turn a PDF into a mangled
 * string before anybody could object.
 */
export const apiV1File = async (
  path: string,
  init: ApiRequestInit = {},
  allowRetry = true,
): Promise<ApiFile> => {
  const res = await send(v1Url(path, init), init, v1Headers(init, '*/*'))

  if (res.status === 401 && allowRetry && !init.anonymous) {
    const token = await requestRefresh()
    if (token) return apiV1File(path, init, false)
    notifyUnauthorized()
  }

  if (!res.ok) {
    /* The failure path *is* JSON: every refusal on `/api/v1` is an error envelope, whatever the
       success path would have been. Reading it is what turns "the plan does not carry this" into
       a sentence instead of a corrupt download. */
    const body = await parseBody(res)
    if (isErrorEnvelope(body)) {
      throw new ApiError(
        res.status,
        body.error.code,
        body.error.message,
        body.error.details,
        errorKeyFor(body.error.code),
      )
    }
    const key = statusKey(res.status)
    throw new ApiError(res.status, `HTTP_${res.status}`, englishText(key), undefined, key)
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const matched = FILENAME.exec(disposition)
  return {
    blob: await res.blob(),
    filename: matched?.[1] ?? 'download',
  }
}

export interface Paged<T> {
  items: T[]
  meta: ApiMeta
}

/** Paginated collection helper — keeps `meta` beside the rows. */
export const apiV1Paged = async <T>(path: string, init: ApiRequestInit = {}): Promise<Paged<T>> => {
  const envelope = await apiV1Envelope<T[]>(path, init)
  return { items: envelope.data, meta: envelope.meta ?? {} }
}

/* ── /api/** — Metricool-compatible, raw ──────────────────────────────────── */

/**
 * Calls a compat endpoint. `userToken`, `userId` and `blogId` are appended from the active
 * session unless the caller passes them explicitly (pass `null` to omit one entirely).
 * The response is returned exactly as the server sent it — no envelope, no renaming.
 */
export const mcApi = async <T>(
  path: string,
  params: QueryParams = {},
  init: ApiRequestInit = {},
): Promise<T> => {
  const merged: QueryParams = { ...params }
  if (!('userToken' in merged) && mcUserToken) merged['userToken'] = mcUserToken
  if (!('userId' in merged) && mcUserId !== null) merged['userId'] = mcUserId
  if (!('blogId' in merged) && activeBlogId !== null) merged['blogId'] = activeBlogId

  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.json !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (mcUserToken) headers.set('X-Mc-Auth', mcUserToken)

  const url = `${API_BASE}/api${path}${buildQuery(merged)}`
  const res = await send(url, init, headers)
  const body = await parseBody(res)

  if (!res.ok) {
    const said = mcErrorText(body, res.status, res.headers.get('X-FTK-Connector-Refusal-Kind'))
    throw new ApiError(res.status, mcErrorCode(body, res.status), said.message, undefined, said.key)
  }
  return body as T
}

const mcErrorCode = (body: unknown, status: number): string => {
  if (typeof body === 'object' && body !== null && 'code' in body) {
    const code = (body as { code: unknown }).code
    if (typeof code === 'string') return code
  }
  return `HTTP_${status}`
}

/**
 * `ReplyRefusal.kind` (`backend/src/compat/v2Inbox.ts`, `X-FTK-Connector-Refusal-Kind`),
 * for the kinds that are this product's own sentence rather than a network's. The compat body
 * cannot carry `kind` itself — `/api/**` is frozen, Metricool-shaped — so it rides a header
 * exactly like `X-FTK-Connector-Persisted` does for the same reason.
 *
 * `refused` is deliberately absent: that `kind` covers both the network's own words
 * (`docs/INGESTION.md` requires those verbatim) and a send that broke in a way this build could
 * not classify, and the header cannot tell the two apart — so both keep the body's sentence
 * exactly as sent, the same fallback every other compat error already gets.
 */
const REFUSAL_KIND_KEYS: Partial<Record<string, SimpleTKey>> = {
  empty: 'inbox.refusal.empty',
  no_connection: 'inbox.refusal.noConnection',
  unsupported: 'inbox.refusal.unsupported',
  not_configured: 'inbox.refusal.notConfigured',
}

/**
 * What the Metricool-compatible surface said, and whether it said anything at all.
 *
 * `key: null` means the words are the server's own and are shown verbatim; a key means we fell
 * back to our own sentence for the status, which is ours to translate.
 */
const mcErrorText = (
  body: unknown,
  status: number,
  refusalKind: string | null,
): { message: string; key: SimpleTKey | null } => {
  const refusalKey = refusalKind !== null ? REFUSAL_KIND_KEYS[refusalKind] : undefined
  if (refusalKey) return { message: englishText(refusalKey), key: refusalKey }

  if (typeof body === 'object' && body !== null) {
    for (const field of ['message', 'error', 'description'] as const) {
      if (field in body) {
        const value = (body as Record<string, unknown>)[field]
        if (typeof value === 'string' && value.length > 0) return { message: value, key: null }
      }
    }
  }
  const key = statusKey(status)
  return { message: englishText(key), key }
}

/* ── error copy for the UI ────────────────────────────────────────────────── */

/**
 * Every error surface renders this — 90-odd call sites, the most-read copy in the product.
 *
 * It reads the locale store at call time rather than taking a `t`, so the ninety call sites did
 * not have to change. Each of them already sits in a component that subscribes to the locale
 * through `useT()` for its own surrounding copy, so a language change repaints the sentence with
 * the panel around it.
 */
export const errorMessage = (error: unknown): string => {
  // A key means somebody translated this failure. No key means the code has no translation, and
  // the server's English is shown as sent — the documented fallback, not a hole.
  if (isApiError(error)) {
    return error.messageKey === null
      ? error.message
      : localeText(error.messageKey, detailParams(error.details))
  }
  if (error instanceof Error && error.message) return error.message
  return localeText('common.errors.unknown')
}
